import {
  DateRangeField,
  MultiselectField,
  Table,
  TextField,
  useFormField,
} from '@weave/design-system';
import { selectCurrentLocation } from '../../../redux/actions/location';
import { useGetLocationEmailsWithLatestStatus } from 'apis/email';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatDate } from 'helpers/date';
import { theme } from '@weave/theme';
import { format, isValid, parse } from 'date-fns';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';

const MAX_ROWS_PER_PAGE = 50;

export const EmailHistoryTable = () => {
  const location = useSelector(selectCurrentLocation);

  const [page, setPage] = useState<number>(1);
  const [cursors, setCursors] = useState<string[]>(['']);

  const dateFormat = 'MM/dd/yyyy';

  const startDate = format(
    new Date(new Date().setDate(new Date().getDate() - 7)),
    dateFormat
  );
  const endDate = format(new Date(), dateFormat);

  const searchFieldProps = useFormField({ type: 'text' });
  const dateRangeFieldProps = useFormField({
    type: 'dateRange',
    value: [startDate, endDate],
  });

  const emailTypeFieldProps = useFormField({
    type: 'multiselect',
  });

  const [debouncedSearch, setDebouncedSearch] = useState(searchFieldProps.value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchFieldProps.value);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchFieldProps.value]);

  const toRFC3339Nano = (dateStr: string): string => {
    const date = parse(dateStr, 'MM/dd/yyyy', new Date());
    if (!isValid(date)) return '';
    return format(date, "yyyy-MM-dd'T'HH:mm:ss") + '.000000000Z';
  };

  const { data: emailDataV2, isLoading: emailDataV2IsLoading } =
    useGetLocationEmailsWithLatestStatus(
      {
        locationId: location?.LocationID,
        cursor: page === 1 ? undefined : cursors[page - 1],
        searchQuery: debouncedSearch || undefined,
        createdAtStart: toRFC3339Nano(dateRangeFieldProps.value[0]),
        createdAtEnd: toRFC3339Nano(dateRangeFieldProps.value[1]),
        emailTypes: (emailTypeFieldProps.value as MessageType_Enum[]) || [],
      },
      {
        enabled: !!location?.LocationID,
      }
    );

  useEffect(() => {
    if (emailDataV2?.nextCursor && cursors[page] !== emailDataV2.nextCursor) {
      setCursors((prev) => {
        const updated = [...prev];
        updated[page] = emailDataV2?.nextCursor ?? '';
        return updated;
      });
    }
  }, [emailDataV2?.nextCursor, page, cursors]);

  const handleNextPage = () => {
    if (emailDataV2?.nextCursor) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  const hasMore = !!emailDataV2?.emailsWithLatestStatus?.[MAX_ROWS_PER_PAGE - 1];

  const formatEnumValue = (value: string): string => {
    return value
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <>
      <div
        css={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <MultiselectField
          css={{
            width: 400,
          }}
          {...emailTypeFieldProps}
          name="email-type"
          label=""
          placeholder="Email Type"
        >
          {Object.values(MessageType_Enum).map((messageType) => (
            <MultiselectField.Option key={messageType} value={messageType}>
              {messageType}
            </MultiselectField.Option>
          ))}
        </MultiselectField>
        <div
          css={{
            margin: theme.spacing(0, 2),
          }}
        >
          <DateRangeField
            css={{
              width: 450,
            }}
            {...dateRangeFieldProps}
            label=""
            name="email-v2-date-range"
          />
        </div>
        <TextField {...searchFieldProps} label="Search" name="email-v2-search" />
      </div>
      <Table
        tableInstanceId="email-history-v2"
        colConfig={[
          {
            Header: 'Email Id',
            id: 'email-id',
            accessor: (row) => row.emailId,
            width: 360,
          },
          {
            Header: 'Email Type',
            id: 'email-type',
            accessor: (row) => formatEnumValue(row.email?.emailType || ''),
          },
          {
            Header: 'Provider',
            id: 'provider',
            accessor: (row) => row.status?.provider?.toLowerCase() || '',
            cellRenderer: (provider) => (
              <span css={{ textTransform: 'capitalize' }}>{provider}</span>
            ),
          },
          {
            Header: 'Provider Message Id',
            id: 'provider-message-id',
            width: 360,
            accessor: (row) => row.status?.providerMessageId || '',
          },
          {
            Header: 'Event',
            id: 'event',
            accessor: (row) => formatEnumValue(row.status?.event?.subtype || ''),
          },
          {
            Header: 'Event Reason',
            id: 'event-reason',
            accessor: (row) => formatEnumValue(row.status?.event?.reason || ''),
          },
          {
            Header: 'To Email',
            id: 'to-email',
            accessor: (row) => row.email?.to[0].email || '',
            width: 285,
          },
          {
            Header: 'From Email',
            id: 'from-email',
            accessor: (row) => row.email?.from?.email || '',
            width: 285,
          },
          {
            Header: 'Reply-To Email',
            id: 'reply-to-email',
            accessor: (row) => row.email?.replyTo?.email || '',
            width: 285,
          },
          {
            Header: 'Subject',
            id: 'subject',
            accessor: (row) => row.email?.subject || '',
          },
          {
            Header: 'Body Media Id',
            id: 'body-media-id',
            accessor: (row) => row.email?.body?.mediaId || '',
            width: 360,
          },
          {
            Header: 'Created By',
            id: 'created-by',
            accessor: (row) => row.email?.createdBy || '',
          },
          {
            Header: 'Created At',
            id: 'created-at',
            accessor: (row) =>
              formatDate(row.emailCreatedAt, 'MMM D, YYYY; hh:mm A z') || '',
            width: 300,
          },
          {
            Header: 'Event Timestamp',
            id: 'event-timestamp',
            accessor: (row) =>
              formatDate(row.status?.event?.timestamp, 'MMM D, YYYY; hh:mm A z') || '',
            width: 300,
          },
        ]}
        data={emailDataV2?.emailsWithLatestStatus ?? []}
        isPaginated={true}
        hasResizeColumns
        hasResponsiveColWidths
        isLoading={emailDataV2IsLoading}
        manualPaginationConfig={{
          showNumRowSelector: false,
          showGoToPageField: false,
          page,
          rowsPerPageOptions: [MAX_ROWS_PER_PAGE],
          handleChange: (prevOrNext: 'prev' | 'next') => {
            if (prevOrNext === 'prev') handlePrevPage();
            if (prevOrNext === 'next') handleNextPage();
          },
          hasNext: hasMore,
          hasPrevious: page !== 1,
        }}
      />
    </>
  );
};
